import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Collapse,
  Menu,
  MenuItem,
  Divider as MuiDivider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LinkIcon from '@mui/icons-material/Link'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WarningIcon from '@mui/icons-material/Warning'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import UnpublishedIcon from '@mui/icons-material/Unpublished'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearIcon from '@mui/icons-material/Clear'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'
import type { Chain, Undertaker } from '../../types'

interface ChainWithLocations extends Chain {
  locations: Undertaker[]
}

export default function UndertakersList() {
  const navigate = useNavigate()
  const { isAdmin, loading: authLoading } = useAuth()
  const [chains, setChains] = useState<ChainWithLocations[]>([])
  const [standaloneUndertakers, setStandaloneUndertakers] = useState<Undertaker[]>([])
  const [loading, setLoading] = useState(true)
  
  // Redirect non-admins to claims page (only after auth is loaded)
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/claims', { replace: true })
    }
  }, [isAdmin, authLoading, navigate])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedChains, setExpandedChains] = useState<Set<string>>(new Set())
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null)
  const [selectedUndertaker, setSelectedUndertaker] = useState<Undertaker | null>(null)
  
  // Filters
  const [filters, setFilters] = useState<string[]>([])
  
  // Add to chain dialog
  const [addToChainDialogOpen, setAddToChainDialogOpen] = useState(false)
  const [selectedChainForAdd, setSelectedChainForAdd] = useState<string>('')
  const [addingToChain, setAddingToChain] = useState(false)
  
  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilters: string[]) => {
    setFilters(newFilters)
  }
  
  const clearFilters = () => {
    setFilters([])
    setSearchTerm('')
  }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Fetch chains with their locations
      const { data: chainsData, error: chainsError } = await supabase
        .from('chains')
        .select('*, locations:undertakers!undertakers_chain_id_fkey(*)')
        .order('name')

      if (chainsError) throw chainsError

      // Fetch standalone undertakers (no chain)
      const { data: standaloneData, error: standaloneError } = await supabase
        .from('undertakers')
        .select('*')
        .is('chain_id', null)
        .order('name')

      if (standaloneError) throw standaloneError

      setChains((chainsData as ChainWithLocations[]) || [])
      setStandaloneUndertakers(standaloneData || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleChain = (chainId: string) => {
    const newExpanded = new Set(expandedChains)
    if (newExpanded.has(chainId)) {
      newExpanded.delete(chainId)
    } else {
      newExpanded.add(chainId)
    }
    setExpandedChains(newExpanded)
  }

  const handleChainMenu = (event: React.MouseEvent<HTMLElement>, chain: Chain) => {
    setAnchorEl(event.currentTarget)
    setSelectedChain(chain)
  }

  const handleUndertakerMenu = (event: React.MouseEvent<HTMLElement>, undertaker: Undertaker) => {
    setAnchorEl(event.currentTarget)
    setSelectedUndertaker(undertaker)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedChain(null)
    setSelectedUndertaker(null)
  }

  const handleDeleteChain = async () => {
    if (!selectedChain) return
    
    if (!window.confirm(`Er du sikker på at du vil slette kæden "${selectedChain.name}"? Lokationerne bliver selvstændige.`)) {
      handleCloseMenu()
      return
    }

    try {
      const { error } = await supabase
        .from('chains')
        .delete()
        .eq('id', selectedChain.id)

      if (error) throw error

      setSuccess('Kæde slettet!')
      fetchData()
    } catch (err: any) {
      setError(err.message)
    } finally {
      handleCloseMenu()
    }
  }

  const handleDeleteUndertaker = async () => {
    if (!selectedUndertaker) return
    
    if (!window.confirm(`Er du sikker på at du vil slette "${selectedUndertaker.name}"?`)) {
      handleCloseMenu()
      return
    }

    try {
      const { error } = await supabase
        .from('undertakers')
        .delete()
        .eq('id', selectedUndertaker.id)

      if (error) throw error

      setSuccess('Bedemand slettet!')
      fetchData()
    } catch (err: any) {
      setError(err.message)
    } finally {
      handleCloseMenu()
    }
  }

  const handleRemoveFromChain = async () => {
    if (!selectedUndertaker) return

    try {
      const { error } = await supabase
        .from('undertakers')
        .update({ chain_id: null })
        .eq('id', selectedUndertaker.id)

      if (error) throw error

      setSuccess('Lokation fjernet fra kæde!')
      fetchData()
    } catch (err: any) {
      setError(err.message)
    } finally {
      handleCloseMenu()
    }
  }

  const handleOpenAddToChainDialog = () => {
    setAddToChainDialogOpen(true)
    setSelectedChainForAdd('')
    handleCloseMenu()
  }

  const handleAddToChain = async () => {
    // ...existing code...
  }

  const handleToggleVerification = async (e: React.MouseEvent, type: 'undertaker' | 'chain', item: any) => {
    e.stopPropagation()
    const newStatus = !item.is_verified
    const table = type === 'chain' ? 'chains' : 'undertakers'
    
    try {
      const { error } = await supabase
        .from(table)
        .update({ is_verified: newStatus })
        .eq('id', item.id)

      if (error) throw error
      
      setSuccess(`${type === 'chain' ? 'Kæde' : 'Bedemand'} opdateret!`)
      fetchData()
    } catch (err: any) {
      setError(err.message)
    }
  }

  // Filter function
  const filterChains = () => {
    let filtered = chains

    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(chain =>
        chain.name.toLowerCase().includes(search) ||
        chain.locations?.some(l =>
          l.name.toLowerCase().includes(search) ||
          l.city?.toLowerCase().includes(search) ||
          l.address?.toLowerCase().includes(search)
        )
      )
    }

    // Apply filters
    if (filters.includes('boosted')) {
      filtered = filtered.filter(chain => chain.global_boost)
    }
    
    if (filters.includes('published')) {
      filtered = filtered.filter(chain => chain.published)
    }
    
    if (filters.includes('unpublished')) {
      filtered = filtered.filter(chain => !chain.published)
    }
    
    if (filters.includes('missing_coords')) {
      filtered = filtered.filter(chain => 
        chain.locations?.some(l => !l.location || !l.location.lat || !l.location.lng)
      )
    }

    if (filters.includes('verified')) {
      filtered = filtered.filter(chain => chain.is_verified)
    }

    if (filters.includes('not_verified')) {
      filtered = filtered.filter(chain => !chain.is_verified)
    }

    return filtered
  }

  const filterStandalone = () => {
    // If chains-only filter is active, hide standalone
    if (filters.includes('chains_only')) {
      return []
    }

    let filtered = standaloneUndertakers

    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(u =>
        u.name.toLowerCase().includes(search) ||
        u.city?.toLowerCase().includes(search) ||
        u.address?.toLowerCase().includes(search)
      )
    }

    // Apply filters
    if (filters.includes('boosted')) {
      filtered = filtered.filter(u => u.global_boost)
    }
    
    if (filters.includes('published')) {
      filtered = filtered.filter(u => u.published)
    }
    
    if (filters.includes('unpublished')) {
      filtered = filtered.filter(u => !u.published)
    }
    
    if (filters.includes('missing_coords')) {
      filtered = filtered.filter(u => !u.location || !u.location.lat || !u.location.lng)
    }

    if (filters.includes('verified')) {
      filtered = filtered.filter(u => u.is_verified)
    }

    if (filters.includes('not_verified')) {
      filtered = filtered.filter(u => !u.is_verified)
    }

    return filtered
  }
  
  // Count boosted items for display
  const countBoosted = () => {
    const boostedChains = chains.filter(c => c.global_boost).length
    const boostedStandalone = standaloneUndertakers.filter(u => u.global_boost).length
    return boostedChains + boostedStandalone
  }
  
  // Count unpublished items
  const countUnpublished = () => {
    const unpubChains = chains.filter(c => !c.published).length
    const unpubStandalone = standaloneUndertakers.filter(u => !u.published).length
    return unpubChains + unpubStandalone
  }
  
  // Count items with missing coordinates
  const countMissingCoords = () => {
    const chainsWithMissing = chains.filter(c => 
      c.locations?.some(l => !l.location || !l.location.lat || !l.location.lng)
    ).length
    const standaloneMissing = standaloneUndertakers.filter(u => 
      !u.location || !u.location.lat || !u.location.lng
    ).length
    return chainsWithMissing + standaloneMissing
  }

  // Count verified items
  const countVerified = () => {
    const verifiedChains = chains.filter(c => c.is_verified).length
    const verifiedStandalone = standaloneUndertakers.filter(u => u.is_verified).length
    return verifiedChains + verifiedStandalone
  }

  // Count not verified items
  const countNotVerified = () => {
    const notVerifiedChains = chains.filter(c => !c.is_verified).length
    const notVerifiedStandalone = standaloneUndertakers.filter(u => !u.is_verified).length
    return notVerifiedChains + notVerifiedStandalone
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  const filteredChains = filterChains()
  const filteredStandalone = filterStandalone()
  const totalLocations = chains.reduce((sum, chain) => sum + (chain.locations?.length || 0), 0) + standaloneUndertakers.length

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Bedemænd ({totalLocations})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {chains.length} kæder • {standaloneUndertakers.length} selvstændige
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={() => navigate('/chains/new')}
            >
              Ny Kæde
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/undertakers/new')}
          >
            Ny Lokation
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Søg på navn, by, adresse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        {/* Filter Toggles */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Filtre:
            </Typography>
          </Box>
          
          <ToggleButtonGroup
            value={filters}
            onChange={handleFilterChange}
            size="small"
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            <ToggleButton 
              value="boosted" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'warning.light',
                  color: 'warning.contrastText',
                  '&:hover': {
                    backgroundColor: 'warning.main',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun boostede kæder/lokationer">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RocketLaunchIcon fontSize="small" />
                  Boostet ({countBoosted()})
                </Box>
              </Tooltip>
            </ToggleButton>
            
            <ToggleButton 
              value="published" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'success.light',
                  color: 'success.contrastText',
                  '&:hover': {
                    backgroundColor: 'success.main',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun publicerede">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon fontSize="small" />
                  Publiceret
                </Box>
              </Tooltip>
            </ToggleButton>
            
            <ToggleButton 
              value="unpublished" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'grey.600',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'grey.700',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun upublicerede">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <UnpublishedIcon fontSize="small" />
                  Upubliceret ({countUnpublished()})
                </Box>
              </Tooltip>
            </ToggleButton>
            
            <ToggleButton 
              value="missing_coords" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'warning.main',
                  color: 'warning.contrastText',
                  '&:hover': {
                    backgroundColor: 'warning.dark',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun med manglende koordinater">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningIcon fontSize="small" />
                  Mangler koordinater ({countMissingCoords()})
                </Box>
              </Tooltip>
            </ToggleButton>

            <ToggleButton 
              value="verified" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'success.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'success.dark',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun verificerede">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon fontSize="small" />
                  Verificeret ({countVerified()})
                </Box>
              </Tooltip>
            </ToggleButton>

            <ToggleButton 
              value="not_verified" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'grey.500',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'grey.600',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun ikke-verificerede">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <UnpublishedIcon fontSize="small" />
                  Ikke verif. ({countNotVerified()})
                </Box>
              </Tooltip>
            </ToggleButton>
            
            <ToggleButton 
              value="chains_only" 
              sx={{ 
                textTransform: 'none',
                borderRadius: '16px !important',
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
              }}
            >
              <Tooltip title="Vis kun kæder (skjul selvstændige)">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LinkIcon fontSize="small" />
                  Kun kæder
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          {(filters.length > 0 || searchTerm) && (
            <Button
              size="small"
              variant="text"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              sx={{ ml: 'auto' }}
            >
              Ryd alle
            </Button>
          )}
        </Box>
        
        {/* Active filter summary */}
        {(filters.length > 0 || searchTerm) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Viser {filteredChains.length} kæder og {filteredStandalone.length} selvstændige
              {filters.includes('boosted') && ' • Kun boostede'}
              {filters.includes('published') && ' • Kun publicerede'}
              {filters.includes('unpublished') && ' • Kun upublicerede'}
              {filters.includes('missing_coords') && ' • Med manglende koordinater'}
              {filters.includes('chains_only') && ' • Kun kæder'}
              {searchTerm && ` • Søgning: "${searchTerm}"`}
            </Typography>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Chains */}
      {filteredChains.map((chain) => (
        <Paper key={chain.id} sx={{ mb: 2 }}>
          {/* Chain Row */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => handleToggleChain(chain.id)}
          >
            <IconButton size="small" sx={{ pointerEvents: 'none' }}>
              {expandedChains.has(chain.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>

            <LinkIcon color="primary" />

            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {chain.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {chain.locations?.length || 0} lokationer
                {chain.locations?.filter(l => l.is_separate_entity).length > 0 && 
                  ` • ${chain.locations.filter(l => l.is_separate_entity).length} separate`
                }
              </Typography>
            </Box>

            {chain.global_boost && (
              <Tooltip title={`Boostet med prioritet ${chain.boost_priority || 0}`}>
                <Chip 
                  icon={<RocketLaunchIcon />} 
                  label={`Boost ${chain.boost_priority || 0}`} 
                  size="small" 
                  color="warning"
                />
              </Tooltip>
            )}

            {chain.rating && (
              <Chip label={`${chain.rating} ⭐`} size="small" variant="outlined" />
            )}

            <Tooltip title={chain.is_verified ? "Klik for at fjerne verificering" : "Klik for at markere som verificeret"}>
              <Chip 
                label={chain.is_verified ? "Verificeret" : "Ikke verif."} 
                color={chain.is_verified ? "success" : "default"}
                size="small" 
                variant={chain.is_verified ? "filled" : "outlined"}
                onClick={(e) => handleToggleVerification(e, 'chain', chain)}
                icon={chain.is_verified ? <CheckCircleIcon /> : undefined}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>

            {chain.published ? (
              <Chip label="Publiceret" color="success" size="small" variant="outlined" />
            ) : (
              <Chip label="Upubliceret" color="default" size="small" variant="outlined" />
            )}

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/chains/edit/${chain.id}`)
              }}
              title="Rediger kæde"
            >
              <EditIcon />
            </IconButton>

            {isAdmin && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleChainMenu(e, chain)
                }}
                title="Flere handlinger"
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>

          {/* Locations (collapsed) */}
          <Collapse in={expandedChains.has(chain.id)}>
            <Box sx={{ pl: 8, pr: 2, pb: 2 }}>
              {chain.locations?.map((location) => (
                <Box
                  key={location.id}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  {(!location.location || !location.location.lat || !location.location.lng) ? (
                    <WarningIcon color="warning" fontSize="small" titleAccess="Mangler koordinater" />
                  ) : (
                    <LocationOnIcon color="success" fontSize="small" titleAccess="Har koordinater" />
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {location.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {location.address}, {location.city}
                      {location.is_separate_entity && ' • Separat profil'}
                    </Typography>
                  </Box>

                  {location.global_boost && (
                    <Tooltip title={`Individuelt boostet med prioritet ${location.boost_priority || 0}`}>
                      <Chip 
                        icon={<RocketLaunchIcon />} 
                        label={`Boost ${location.boost_priority || 0}`} 
                        size="small" 
                        color="warning"
                      />
                    </Tooltip>
                  )}

                  <Tooltip title={location.is_verified ? "Klik for at fjerne verificering" : "Klik for at markere som verificeret"}>
                    <Chip 
                      label={location.is_verified ? "Verificeret" : "Ikke verif."} 
                      color={location.is_verified ? "success" : "default"}
                      size="small" 
                      variant={location.is_verified ? "filled" : "outlined"}
                      onClick={(e) => handleToggleVerification(e, 'undertaker', location)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>

                  {location.is_separate_entity && (
                    <Chip label="★ Separat" size="small" color="secondary" variant="outlined" />
                  )}

                  <IconButton
                    size="small"
                    onClick={() => navigate(`/undertakers/edit/${location.id}`)}
                    title="Rediger"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  {isAdmin && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleUndertakerMenu(e, location)}
                      title="Flere handlinger"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>
        </Paper>
      ))}

      {/* Standalone Undertakers */}
      {filteredStandalone.length > 0 && (
        <>
          <MuiDivider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              SELVSTÆNDIGE LOKATIONER ({filteredStandalone.length})
            </Typography>
          </MuiDivider>

          {filteredStandalone.map((undertaker) => (
            <Paper
              key={undertaker.id}
              sx={{
                p: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {(!undertaker.location || !undertaker.location.lat || !undertaker.location.lng) ? (
                <WarningIcon color="warning" titleAccess="Mangler koordinater" />
              ) : (
                <LocationOnIcon color="success" titleAccess="Har koordinater" />
              )}

              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {undertaker.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {undertaker.address}, {undertaker.city}
                </Typography>
              </Box>

              {undertaker.global_boost && (
                <Tooltip title={`Boostet med prioritet ${undertaker.boost_priority || 0}`}>
                  <Chip 
                    icon={<RocketLaunchIcon />} 
                    label={`Boost ${undertaker.boost_priority || 0}`} 
                    size="small" 
                    color="warning"
                  />
                </Tooltip>
              )}

              <Tooltip title={undertaker.is_verified ? "Klik for at fjerne verificering" : "Klik for at markere som verificeret"}>
                <Chip 
                  label={undertaker.is_verified ? "Verificeret" : "Ikke verif."} 
                  color={undertaker.is_verified ? "success" : "default"}
                  size="small" 
                  variant={undertaker.is_verified ? "filled" : "outlined"}
                  onClick={(e) => handleToggleVerification(e, 'undertaker', undertaker)}
                  icon={undertaker.is_verified ? <CheckCircleIcon /> : undefined}
                  sx={{ cursor: 'pointer' }}
                />
              </Tooltip>

              {undertaker.published ? (
                <Chip label="Publiceret" color="success" size="small" variant="outlined" />
              ) : (
                <Chip label="Upubliceret" color="default" size="small" variant="outlined" />
              )}

              <IconButton
                size="small"
                onClick={() => navigate(`/undertakers/edit/${undertaker.id}`)}
                title="Rediger"
              >
                <EditIcon />
              </IconButton>

              {isAdmin && (
                <IconButton
                  size="small"
                  onClick={(e) => handleUndertakerMenu(e, undertaker)}
                  title="Flere handlinger"
                >
                  <MoreVertIcon />
                </IconButton>
              )}
            </Paper>
          ))}
        </>
      )}

      {filteredChains.length === 0 && filteredStandalone.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Ingen bedemænd fundet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchTerm ? 'Prøv en anden søgning' : 'Klik på "Ny Lokation" for at oprette'}
          </Typography>
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {selectedChain && (
          <MenuItem onClick={handleDeleteChain}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Slet kæde
          </MenuItem>
        )}
        {selectedUndertaker && !selectedUndertaker.chain_id && chains.length > 0 && (
          <MenuItem onClick={handleOpenAddToChainDialog}>
            <LinkIcon fontSize="small" sx={{ mr: 1 }} />
            Tilføj til kæde
          </MenuItem>
        )}
        {selectedUndertaker && selectedUndertaker.chain_id && (
          <MenuItem onClick={handleRemoveFromChain}>
            <LinkIcon fontSize="small" sx={{ mr: 1 }} />
            Fjern fra kæde
          </MenuItem>
        )}
        {selectedUndertaker && (
          <MenuItem onClick={handleDeleteUndertaker}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Slet lokation
          </MenuItem>
        )}
      </Menu>

      {/* Add to Chain Dialog */}
      <Dialog 
        open={addToChainDialogOpen} 
        onClose={() => setAddToChainDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Tilføj til kæde
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vælg hvilken kæde "{selectedUndertaker?.name}" skal tilføjes til.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="add-to-chain-label">Vælg kæde</InputLabel>
            <Select
              labelId="add-to-chain-label"
              value={selectedChainForAdd}
              label="Vælg kæde"
              onChange={(e) => setSelectedChainForAdd(e.target.value)}
            >
              {chains.map((chain) => (
                <MenuItem key={chain.id} value={chain.id}>
                  {chain.name} ({chain.locations?.length || 0} lokationer)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            Når lokationen tilføjes til kæden, vil den som standard arve kædens profildata (beskrivelse, kontakt, sociale medier). 
            Du kan senere markere den som "separat profil" hvis den skal have egne data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToChainDialogOpen(false)} disabled={addingToChain}>
            Annuller
          </Button>
          <Button 
            onClick={handleAddToChain} 
            variant="contained"
            disabled={!selectedChainForAdd || addingToChain}
          >
            {addingToChain ? 'Tilføjer...' : 'Tilføj til kæde'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

